import React from 'react';
import { EventEmitter } from 'events';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { SharedSettingsManager } from '../services/SharedSettingsManager';
import { Hotkey as HotkeyDefinition, HotkeyManager as CoreHotkeyManager } from '../../hotkeys/Hotkeys';

/**
 * Modern, scalable plugin system for MapEditor
 * Combines React patterns with imperative APIs for maximum flexibility
 */

// Core plugin lifecycle and capability interface
export interface IMapEditorPlugin {
  readonly metadata: PluginMetadata;
  readonly capabilities: PluginCapabilities;
  
  // Lifecycle hooks
  initialize(context: PluginContext): Promise<void>;
  activate(): Promise<void>;
  deactivate(): Promise<void>;
  dispose(): Promise<void>;
  
  // Event handlers (return true if event was handled/consumed)
  onMapClick?(event: MapInteractionEvent): Promise<boolean>;
  onMapDrag?(event: MapDragEvent): Promise<boolean>;
  onMapHover?(event: MapHoverEvent): Promise<boolean>;
  onKeyDown?(event: KeyboardEvent): Promise<boolean>;
  onKeyUp?(event: KeyboardEvent): Promise<boolean>;
  
  // Tool-specific operations
  canHandle?(operation: MapOperation): boolean;
  execute?(operation: MapOperation): Promise<MapOperationResult>;
  preview?(operation: MapOperation): Promise<PreviewResult>;
  validate?(operation: MapOperation): Promise<ValidationResult>;
}

// Plugin metadata and configuration
export interface PluginMetadata {
  id: string;
  name: string;
  description: string;
  version: string;
  author?: string;
  category: PluginCategory;
  icon: React.ReactElement<SvgIconProps>;
  dependencies?: string[];
  optionalDependencies?: string[];
  
  // UI configuration
  enabledByDefault?: boolean;
  showInToolbar?: boolean;
  toolbarOrder?: number;
}

// Plugin capabilities flags
export interface PluginCapabilities {
  // Tool capabilities
  canPaintTiles?: boolean;
  canSelectTiles?: boolean;
  canEditObjects?: boolean;
  canModifyTerrain?: boolean;
  
  // UI capabilities
  hasToolbarComponent?: boolean;
  hasSidebarComponent?: boolean;
  hasPropertiesPanel?: boolean;
  hasSettingsPanel?: boolean;
  
  // System capabilities
  requiresMapAccess?: boolean;
  requiresHotkeys?: boolean;
  requiresPointerEvents?: boolean;
  canExportData?: boolean;
  canImportData?: boolean;
}

// Plugin categories for organization
export enum PluginCategory {
  PAINTING = 'painting',
  SELECTION = 'selection', 
  OBJECTS = 'objects',
  TERRAIN = 'terrain',
  UTILITIES = 'utilities',
  IMPORT_EXPORT = 'import_export',
  ANALYSIS = 'analysis',
  EFFECTS = 'effects'
}

// Comprehensive plugin context
export interface PluginContext {
  // Core services
  mapService: MapEditorService;
  hotkeyManager: CoreHotkeyManager;
  eventBus: EventEmitter;
  
  // Centralized input and cursor management
  cursorManager?: any; // CentralizedCursorManager (optional for backward compatibility)
  inputCoordinator?: any; // InputCoordinator (optional for backward compatibility)
  cursorHighlightManager?: any; // CursorHighlightManager (optional for backward compatibility)
  brushSettingsManager?: any; // BrushSettingsManager (optional for backward compatibility)
  brushSettingsBridge?: any; // BrushSettingsBridge (optional for backward compatibility)
  cursorLifecycleManager?: any; // CursorLifecycleManager (optional for backward compatibility)
  
  // State management
  editorState: MapEditorState;
  layerManager: LayerManager;
  selectionManager: SelectionManager;
  undoRedoManager: UndoRedoManager;
  previewManager: PreviewManager;
  
  // Map interaction
  mapPointers: MapPointerManager;
  
  // Shared settings for cross-plugin compatibility
  sharedSettings: SharedSettingsManager;
  
  // UI services
  notificationService: NotificationService;
  dialogService: DialogService;
  statusBarService: StatusBarService;
  
  // Utility functions
  getTileAt(x: number, y: number, layer?: string): number | null;
  setTileAt(x: number, y: number, tileId: number, layer?: string): void;
  getMapData(): any;
  getLayerData(layerId: string): any;
  invalidateRegion(x: number, y: number, width: number, height: number): void;
  
  // Plugin communication
  getPlugin<T extends IMapEditorPlugin>(pluginId: string): T | null;
  broadcastEvent(eventName: string, data: any): void;
}

// Map interaction events
export interface MapInteractionEvent {
  type: 'click' | 'rightclick' | 'doubleclick' | 'drag';
  worldX: number;
  worldY: number;
  tileX: number;
  tileY: number;
  pointer: Phaser.Input.Pointer;
  modifiers: EventModifiers;
  layer: string;
  preventDefault(): void;
  stopPropagation(): void;
}

export interface MapDragEvent extends MapInteractionEvent {
  type: 'drag';
  deltaX: number;
  deltaY: number;
  startX: number;
  startY: number;
  isDragging: boolean;
}

export interface MapHoverEvent {
  type: 'hover';
  worldX: number;
  worldY: number;
  tileX: number;
  tileY: number;
  pointer: Phaser.Input.Pointer;
  layer: string;
}

export interface EventModifiers {
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
  meta: boolean;
}

// Operation types and results
export interface MapOperation {
  type: 'paint' | 'erase' | 'fill' | 'select' | 'move' | 'rotate' | 'delete' | 'create';
  layer: string;
  positions: Array<{ x: number; y: number }>;
  data?: any; // Operation-specific data
  metadata?: any; // Additional metadata
}

export interface MapOperationResult {
  success: boolean;
  changes: Array<{
    layer: string;
    position: { x: number; y: number };
    oldValue: any;
    newValue: any;
  }>;
  undoable: boolean;
}

export interface PreviewResult {
  visible: boolean;
  preview?: Phaser.GameObjects.GameObject[];
  cursor?: string;
}

export interface ValidationResult {
  valid: boolean;
  errors?: string[];
  warnings?: string[];
}

// UI Component interfaces
export interface ToolbarComponentProps {
  plugin: IMapEditorPlugin;
  context: PluginContext;
  isActive: boolean;
}

export interface SidebarComponentProps {
  plugin: IMapEditorPlugin;
  context: PluginContext;
}

export interface PropertiesPanelProps {
  plugin: IMapEditorPlugin;
  context: PluginContext;
  selection?: any;
}

export interface SettingsPanelProps {
  plugin: IMapEditorPlugin;
  context: PluginContext;
  settings: any;
  onSettingsChange: (settings: any) => void;
}

// React component getters for plugins
export interface PluginComponents {
  ToolbarComponent?: React.ComponentType<ToolbarComponentProps>;
  SidebarComponent?: React.ComponentType<SidebarComponentProps>;
  PropertiesComponent?: React.ComponentType<PropertiesPanelProps>;
  SettingsComponent?: React.ComponentType<SettingsPanelProps>;
}

// Plugin registration descriptor
export interface PluginDescriptor extends PluginMetadata {
  // Factory function to create plugin instance
  createPlugin: (context: PluginContext) => IMapEditorPlugin;
  
  // React components (can be provided directly or through plugin instance)
  components?: PluginComponents;
}

// Services that plugins can access
export interface MapEditorService {
  // ... existing MapEditorService interface
}

export type HotkeyManager = CoreHotkeyManager;

export interface MapPointerManager {
  registerHandler(pluginId: string, handler: PointerHandler): void;
  unregisterHandler(pluginId: string): void;
  setActiveHandler(pluginId: string): void;
  setActiveCursor(cursor: string): void;
  resetCursor(): void;
}

export interface PointerHandler {
  onPointerDown?(event: MapInteractionEvent): Promise<boolean>;
  onPointerMove?(event: MapInteractionEvent): Promise<boolean>;
  onPointerUp?(event: MapInteractionEvent): Promise<boolean>;
  onPointerWheel?(event: WheelEvent): Promise<boolean>;
}

// Service interfaces
export interface NotificationService {
  show(message: string, type: 'success' | 'error' | 'warning' | 'info', duration?: number): void;
  showProgress(message: string, progress: number): void;
  hide(id?: string): void;
}

export interface DialogService {
  showDialog<T = any>(component: React.ComponentType, props?: any): Promise<T>;
  showConfirm(message: string, title?: string): Promise<boolean>;
  showPrompt(message: string, defaultValue?: string): Promise<string | null>;
}

export interface StatusBarService {
  setStatus(status: Partial<StatusBarState>): void;
  clearStatus(): void;
}

export interface StatusBarState {
  message?: string;
  coordinates?: { x: number; y: number };
  layer?: string;
  tool?: string;
  selection?: string;
}

// Manager interfaces (simplified)
export interface LayerManager {
  getActiveLayers(): LayerInfo[];
  setActiveLayer(layerId: string): void;
  getLayerVisibility(layerId: string): boolean;
  setLayerVisibility(layerId: string, visible: boolean): void;
}

export interface SelectionManager {
  getSelection(): any;
  setSelection(selection: any): void;
  clearSelection(): void;
  hasSelection(): boolean;
}

export interface UndoRedoManager {
  execute(operation: MapOperation): Promise<void>;
  undo(): Promise<boolean>;
  redo(): Promise<boolean>;
  canUndo(): boolean;
  canRedo(): boolean;
}

export interface PreviewManager {
  showPreview(preview: Phaser.GameObjects.GameObject[]): void;
  hidePreview(): void;
  updatePreview(preview: Phaser.GameObjects.GameObject[]): void;
}

// Re-export types that are used but defined elsewhere
export interface LayerInfo {
  name: string;
  type: 'tile' | 'object';
  visible: boolean;
  opacity: number;
  zIndex?: number;
}

export interface MapEditorState {
  activeTool: string;
  selectedTile: any | null;
  activeLayer: string | null;
  brushSettings: any;
  layerVisibility: { [layerName: string]: boolean };
  layerOpacity: { [layerName: string]: number };
  showGrid: boolean;
  gridSize: number;
  snapToGrid: boolean;
  undoHistory: MapOperation[];
  redoHistory: MapOperation[];
  isEditing: boolean;
  selectedObject?: Phaser.GameObjects.GameObject | null;
} 

// Re-export HotkeyDefinition so other modules can import it directly from this file
export type { HotkeyDefinition }; 